import React, { useMemo } from "react";
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
} from "recharts";

export default function ResinLineChart({
  rawData,
  lineChartactualData,
  forecastStartLabel,
  risiSeriesLineChartIndex,
  firstForecastYearMonth,
  firstForecastKeysToBeMergedActualLAst,
  currencySymbol = '$',
  isShowProductInTooltip = false,
  useSimpleTooltip = false,
  yAxisLabel = "",
}) {
  const COL_WIDTH = 50;
  const VISIBLE_MIN_WIDTH = 1000;

  const forecastStart = forecastStartLabel;
  const forecastIndex = rawData?.findIndex((d) => d.yearMonth === forecastStart);

  const { baseData, actualDataBySeries, forecastDataBySeries } = useMemo(() => {
    const baseData = rawData?.map((r, index) => ({ ...r, x: index + 1 }));

    const actualDataBySeries = {};
    const forecastDataBySeries = {};

    risiSeriesLineChartIndex?.forEach((s) => {
      const lastActualIndex = lineChartactualData?.findLastIndex(
        (row) => row[s.key] != null
      );

      actualDataBySeries[s.key] = baseData.map((row, i) => ({
        x: row.x,
        yearMonth: row.yearMonth,
        assessmentDate: row.assessmentDate,
        forecastActualMergedVal:
          row.yearMonth === firstForecastYearMonth ? firstForecastYearMonth : "",
        [s.key]: i <= lastActualIndex + 1 ? row[s.key] : null,
      }));

      if (forecastIndex !== -1) {
        forecastDataBySeries[s.key] = baseData.map((row, i) => ({
          x: row.x,
          yearMonth: row.yearMonth,
          assessmentDate: row.assessmentDate,
          [s.key]:
            i === lastActualIndex
              ? row[s.key]
              : i > lastActualIndex
              ? row[s.key]
              : null,
        }));
      }
    });

    return { baseData, actualDataBySeries, forecastDataBySeries };
  }, [rawData, forecastStartLabel]);

  const CustomizedTick = ({ x, y, payload }) => {
    const matchedData = baseData?.find((d) => d.x === payload.value);
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={5}
          textAnchor="end"
          fill="#616161"
          style={{ fontSize: "12px" }}
          transform="rotate(-90)"
        >
          {matchedData?.yearMonth}
        </text>
      </g>
    );
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !label) return null;

    const idx = baseData?.findIndex((d) => d.x === label);
    if (idx === -1) return null;

    if (useSimpleTooltip) {
      const yearMonthLabel = payload[0]?.payload?.yearMonth;
      const simpleItems = risiSeriesLineChartIndex?.map((s) => {
        const actualArr = actualDataBySeries?.[s.key] || [];
        const forecastArr = forecastDataBySeries?.[s.key] || [];
        const actualVal = actualArr[idx]?.[s.key] ?? null;
        const forecastVal = forecastArr[idx]?.[s.key] ?? null;
        const value = actualVal != null ? actualVal : forecastVal;
        return { key: s.key, name: s.name || s.key, color: s.color, value };
      });

      return (
        <div
          style={{
            backgroundColor: "#fff",
            padding: "10px",
            border: "1px solid #ccc",
            maxWidth: 220,
            boxSizing: "border-box",
            wordBreak: "break-word",
            overflowWrap: "break-word",
          }}
        >
          <p style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
            <strong>{yearMonthLabel}</strong>
          </p>
          {simpleItems
            ?.filter((it) => it.value != null)
            ?.map((it) => (
              <p
                key={it.key}
                style={{ color: it.color, whiteSpace: "normal", wordBreak: "break-word" }}
              >
                {it.name}: {it.value}
              </p>
            ))}
        </div>
      );
    }

    const items = risiSeriesLineChartIndex?.map((s) => {
      const actualArr = actualDataBySeries?.[s.key] || [];
      const forecastArr = forecastDataBySeries?.[s.key] || [];

      const actualVal = actualArr[idx]?.[s.key] ?? null;
      const forecastVal = forecastArr[idx]?.[s.key] ?? null;

      const lastActualIndex = lineChartactualData?.findLastIndex(
        (row) => row?.[s.key] != null
      );
      const isForecastMonthForSeries = idx > lastActualIndex;

      const value = isForecastMonthForSeries
        ? forecastVal != null
          ? forecastVal
          : actualVal
        : actualVal != null
        ? actualVal
        : forecastVal;

      const isForecastActualLabelMerged =
        firstForecastKeysToBeMergedActualLAst?.includes(s.key) &&
        firstForecastYearMonth === actualArr?.[idx]?.["forecastActualMergedVal"];

      const stringVal = isForecastActualLabelMerged
        ? "Price (Forecast)"
        : isForecastMonthForSeries
        ? "Price (Forecast)"
        : "Price (Actual)";

      return { key: s.key, color: s.color, stringVal, value };
    });

    return (
      <div
        style={{
          background: "#fff",
          border: "1px solid #ccc",
          borderRadius: 6,
          padding: 10,
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          fontSize: 13,
        }}
      >
        {items
          ?.filter((it) => it.value != null)
          ?.map((it) => (
            <div
              key={it.key}
              style={{ marginBottom: 8, display: "flex", gap: 8, alignItems: "center" }}
            >
              <div>
                <div>
                  <strong>{isShowProductInTooltip ? "Product:" : "Grade:"}</strong> {it.key}
                </div>
                <div>
                  <strong>Assessment Date:</strong>{" "}
                  {payload[0]?.payload?.assessmentDate}
                </div>
                <div>
                  <strong>{it.stringVal}:</strong> {it.value}
                </div>
                <div>
                  <strong>Date:</strong> {payload[0]?.payload?.yearMonth}
                </div>
              </div>
            </div>
          ))}
      </div>
    );
  };
  const isScrollable = baseData?.length > 70;
  const chartWidth = isScrollable
    ? Math.max(baseData.length * COL_WIDTH, VISIBLE_MIN_WIDTH)
    : "100%";
  // console.log("baseData", baseData);
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', width: '100%' }}>
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', width: '100%', overflow: 'hidden' }}>
    <div style={{ width: '100%', minWidth: 0, overflowX: isScrollable ? 'auto' : 'hidden', overflowY: 'hidden' }}>
    <ResponsiveContainer width={chartWidth} minWidth={isScrollable ? chartWidth : undefined} height={350}>
      <ComposedChart
        data={baseData}
        // margin={{ top: 20, right: 40, left: 0, bottom: 20 }}
        margin={{ top: 20, right: 40, left: (useSimpleTooltip && yAxisLabel)?10:-15, bottom: 30 }}
      >
        <CartesianGrid horizontal={true} vertical={false} stroke="#DFDFDF" />
        <XAxis
          dataKey="x"
          type="number"
          interval={0}
          tick={<CustomizedTick />}
          domain={["dataMin", "dataMax"]}
          ticks={baseData?.map((d) => d.x)}
          axisLine={false}
          tickLine={false}
          padding={{ left: 40, right: 20 }}
        />
        
                <YAxis
          axisLine={false}
          tickLine={false}
          tickFormatter={(v) => `${v}`}
          label={
            useSimpleTooltip && yAxisLabel
              ? {
                  value: yAxisLabel,
                  angle: -90,
                  position: "insideLeft",
                  style: { fontSize: 16, fill: "#4b4b4b", textAnchor: "middle", fontWeight: 600 },
                }
              : undefined
          }
        />

        <Tooltip
          content={<CustomTooltip />}
          allowEscapeViewBox={useSimpleTooltip ? { x: false, y: false } : undefined}
        />

        {risiSeriesLineChartIndex?.map((s) => (
          <React.Fragment key={s.key}>
            <Line
              data={actualDataBySeries[s.key]}
              dataKey={s.key}
              name={`${s.key} (Actual)`}
              stroke="#1f77b4"
              strokeWidth={2}
              dot={{ fill: "#1f77b4", stroke: "#1f77b4", r: 2 }}
              isAnimationActive={false}
              connectNulls={false}
            />
            {forecastDataBySeries[s.key] && (
              <Line
                data={forecastDataBySeries[s.key]}
                dataKey={s.key}
                name={`${s.key} (Forecast)`}
                stroke="#1f77b4"
                strokeWidth={2}
                strokeDasharray="6 4"
                dot={{ fill: "#1f77b4", stroke: "#1f77b4", r: 2 }}
                isAnimationActive={false}
                connectNulls={false}
              />
            )}
          </React.Fragment>
        ))}
      </ComposedChart>
    </ResponsiveContainer>
    </div>
    </div>
    {/* Custom Legend */}
    {
      useSimpleTooltip && (
        <>
          <br/>
          <br/>
        </>
      )
    }
    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'left', gap: '6px 16px', padding: '8px 16px 4px' }}>
      {risiSeriesLineChartIndex?.map((s, index) => (
        <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}>
          <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', backgroundColor: '#1f77b4', flexShrink: 0 }} />
          <span style={{ fontSize: 12, color: '#4B4B4B', fontFamily: 'Inter', whiteSpace:"normal" }}>{s.name || s.key}</span>
        </div>
      ))}
    </div>
    </div>
  );
}
